export const PRIORITY = {
  ACTIVE: 0,
  LINKED: 1,
  INTENT: 2,
};

export const TOUR_PERF = {
  linkedPrefetchLimit: 2,
  intentPrefetchLimit: 1,
  maxActiveLoads: 2,
  maxSpeculativeLoads: 1,
  bytesPerPixel: 4,
  defaultBudgetBytes: 96 * 1024 * 1024,
  lowBudgetBytes: 48 * 1024 * 1024,
  highBudgetBytes: 160 * 1024 * 1024,
  minPixelRatio: 1,
  pixelRatioStep: 0.25,
  poorFrameMs: 22,
  goodFrameMs: 14,
  poorDurationMs: 800,
  goodDurationMs: 2500,
  dprCooldownMs: 1500,
  ignoreFrameMs: 80,
  staleReleaseVelocityMs: 48,
  crossfadeMs: 220,
  maxPanoramaWidthDesktop: 4096,
  maxPanoramaWidthLow: 2048,
};

export const normalizePanoramaUrl = url => {
  if (!url) return "";
  try {
    return encodeURI(decodeURI(url));
  } catch {
    return url;
  }
};

export const estimateTextureBytes = (
  width,
  height,
  { generateMipmaps = false } = {}
) => {
  const base =
    Math.max(1, width || 1) * Math.max(1, height || 1) * TOUR_PERF.bytesPerPixel;
  return generateMipmaps ? Math.round(base * 4 / 3) : base;
};

export const getTextureByteBudget = ({
  deviceMemory,
  innerWidth,
} = {}) => {
  if (typeof deviceMemory === "number" && deviceMemory > 0 && deviceMemory <= 4) {
    return TOUR_PERF.lowBudgetBytes;
  }
  if (typeof innerWidth === "number" && innerWidth > 0 && innerWidth < 768) {
    return TOUR_PERF.lowBudgetBytes;
  }
  if (typeof deviceMemory === "number" && deviceMemory >= 8) {
    return TOUR_PERF.highBudgetBytes;
  }
  return TOUR_PERF.defaultBudgetBytes;
};

export const getMaxPanoramaWidth = ({
  deviceMemory,
  innerWidth,
} = {}) => {
  if (typeof deviceMemory === "number" && deviceMemory > 0 && deviceMemory <= 4) {
    return TOUR_PERF.maxPanoramaWidthLow;
  }
  if (typeof innerWidth === "number" && innerWidth > 0 && innerWidth < 768) {
    return TOUR_PERF.maxPanoramaWidthLow;
  }
  return TOUR_PERF.maxPanoramaWidthDesktop;
};

export const getInitialPixelRatio = ({
  devicePixelRatio = 1,
  deviceMemory,
} = {}) => {
  const dpr = devicePixelRatio || 1;
  if (typeof deviceMemory === "number" && deviceMemory > 0 && deviceMemory <= 4) {
    return Math.min(dpr, 1);
  }
  if (typeof deviceMemory === "number" && deviceMemory >= 8) {
    return Math.min(dpr, 1.75);
  }
  return Math.min(dpr, 1.5);
};

export const shouldEnableAntialias = (nativeDevicePixelRatio = 1) =>
  (nativeDevicePixelRatio || 1) <= 1;

export const shouldApplyLoad = (gen, currentGen, mounted) =>
  Boolean(mounted) && gen === currentGen;

export const velocityOnRelease = (
  velLon,
  velLat,
  lastMoveAt,
  now,
  staleMs = TOUR_PERF.staleReleaseVelocityMs
) => {
  if (lastMoveAt == null || now - lastMoveAt > staleMs) {
    return { velLon: 0, velLat: 0 };
  }
  return { velLon, velLat };
};

export const getLinkedSceneUrls = (
  scene,
  scenes = [],
  limit = TOUR_PERF.linkedPrefetchLimit
) => {
  const urls = [];
  const seen = new Set();
  const self = normalizePanoramaUrl(scene?.imageUrl);
  if (self) seen.add(self);
  for (const hotspot of scene?.hotspots ?? []) {
    if (urls.length >= limit) break;
    const target = scenes.find(item => item.id === hotspot.targetSceneId);
    const url = normalizePanoramaUrl(target?.imageUrl);
    if (!url || seen.has(url)) continue;
    seen.add(url);
    urls.push(url);
  }
  return urls;
};

export const createQualityController = ({
  initialRatio,
  minRatio = TOUR_PERF.minPixelRatio,
  maxRatio,
  now = () => performance.now(),
} = {}) => {
  let ratio = initialRatio;
  let ema = 16.6;
  let poorSince = null;
  let goodSince = null;
  let lastChange = 0;

  return {
    getRatio: () => ratio,
    resetTiming: () => {
      ema = 16.6;
      poorSince = null;
      goodSince = null;
    },
    sample(dtMs, { interacting } = {}) {
      const t = now();
      if (!interacting) {
        poorSince = null;
        goodSince = null;
        return null;
      }
      if (!(dtMs > 0) || dtMs > TOUR_PERF.ignoreFrameMs) return null;

      ema = ema * 0.85 + dtMs * 0.15;
      if (t - lastChange < TOUR_PERF.dprCooldownMs) return null;

      if (ema > TOUR_PERF.poorFrameMs) {
        goodSince = null;
        if (poorSince == null) poorSince = t;
        if (t - poorSince >= TOUR_PERF.poorDurationMs && ratio > minRatio + 0.001) {
          ratio = Math.max(
            minRatio,
            Math.round((ratio - TOUR_PERF.pixelRatioStep) * 100) / 100
          );
          lastChange = t;
          poorSince = null;
          return ratio;
        }
      } else if (ema < TOUR_PERF.goodFrameMs) {
        poorSince = null;
        if (goodSince == null) goodSince = t;
        if (t - goodSince >= TOUR_PERF.goodDurationMs && ratio < maxRatio - 0.001) {
          ratio = Math.min(
            maxRatio,
            Math.round((ratio + TOUR_PERF.pixelRatioStep * 0.6) * 100) / 100
          );
          lastChange = t;
          goodSince = null;
          return ratio;
        }
      } else {
        poorSince = null;
        goodSince = null;
      }
      return null;
    },
  };
};

const isAbortError = err =>
  err?.name === "AbortError" ||
  (typeof DOMException !== "undefined" && err?.code === DOMException.ABORT_ERR);

export const createPanoramaRuntime = ({
  loadTexture,
  getBudget = () =>
    getTextureByteBudget({
      deviceMemory:
        typeof navigator !== "undefined" ? navigator.deviceMemory : undefined,
      innerWidth: typeof window !== "undefined" ? window.innerWidth : undefined,
    }),
  estimateBytes = texture =>
    estimateTextureBytes(texture?.image?.width, texture?.image?.height, {
      generateMipmaps: Boolean(texture?.generateMipmaps),
    }),
  disposeTexture = texture => {
    texture?.dispose?.();
  },
  now = () =>
    typeof performance !== "undefined" ? performance.now() : Date.now(),
} = {}) => {
  const cache = new Map();
  const queue = [];
  const inFlight = new Map();
  let navActive = 0;
  let specActive = 0;
  let paused = false;

  const touch = url => {
    const entry = cache.get(url);
    if (!entry) return;
    cache.delete(url);
    cache.set(url, entry);
    entry.lastUsed = now();
  };

  const cachedBytes = () => {
    let total = 0;
    for (const entry of cache.values()) {
      if (entry.texture) total += entry.bytes || 0;
    }
    return total;
  };

  const disposeEntry = url => {
    const entry = cache.get(url);
    if (!entry) return;
    if (entry.texture) disposeTexture(entry.texture);
    cache.delete(url);
  };

  const acquire = (url, ownerId) => {
    const key = normalizePanoramaUrl(url);
    if (!key || ownerId == null) return;
    let entry = cache.get(key);
    if (!entry) {
      entry = {
        url: key,
        texture: null,
        bytes: 0,
        refs: 0,
        owners: new Map(),
        lastUsed: now(),
        warmed: new Set(),
      };
      cache.set(key, entry);
    }
    const prev = entry.owners.get(ownerId) || 0;
    entry.owners.set(ownerId, prev + 1);
    entry.refs += 1;
    touch(key);
  };

  const release = (url, ownerId) => {
    const key = normalizePanoramaUrl(url);
    const entry = cache.get(key);
    if (!entry || ownerId == null) return;
    const prev = entry.owners.get(ownerId) || 0;
    if (prev <= 1) entry.owners.delete(ownerId);
    else entry.owners.set(ownerId, prev - 1);
    entry.refs = Math.max(0, entry.refs - 1);
  };

  const prune = (protectUrls = []) => {
    const protect = new Set(
      (protectUrls || []).map(normalizePanoramaUrl).filter(Boolean)
    );
    let total = cachedBytes();
    const budget = getBudget();
    while (total > budget) {
      let victim = null;
      for (const [url, entry] of cache) {
        if (!entry.texture) continue;
        if (entry.refs > 0) continue;
        if (protect.has(url)) continue;
        if (inFlight.has(url)) continue;
        victim = url;
        break;
      }
      if (!victim) break;
      total -= cache.get(victim).bytes || 0;
      disposeEntry(victim);
    }
    return cachedBytes();
  };

  const rememberTexture = (url, texture) => {
    let entry = cache.get(url);
    if (!entry) {
      entry = {
        url,
        texture: null,
        bytes: 0,
        refs: 0,
        owners: new Map(),
        lastUsed: now(),
        warmed: new Set(),
      };
      cache.set(url, entry);
    }
    entry.texture = texture;
    entry.bytes = estimateBytes(texture);
    entry.lastUsed = now();
    touch(url);
    prune([url]);
    return entry;
  };

  const canStart = speculative => {
    const total = navActive + specActive;
    if (total >= TOUR_PERF.maxActiveLoads) return false;
    if (speculative) return specActive < TOUR_PERF.maxSpeculativeLoads;
    return navActive < TOUR_PERF.maxActiveLoads;
  };

  const abortJob = (url, reason = "cancelled") => {
    const job = inFlight.get(url);
    if (!job) return;
    if (job.owners.size > 0) return;
    try {
      job.abortController.abort(reason);
    } catch {
      job.abortController.abort();
    }
  };

  const ensureNavSlot = () => {
    if (navActive + specActive < TOUR_PERF.maxActiveLoads) return;
    if (navActive >= TOUR_PERF.maxActiveLoads) return;
    for (const [url, job] of inFlight) {
      if (!job.speculative || job.owners.size > 0) continue;
      abortJob(url, "preempted");
      break;
    }
  };

  const startJob = item => {
    const { url, speculative, priority } = item;
    const existing = inFlight.get(url);
    if (existing && !existing.abortController?.signal?.aborted) return;
    if (existing?.abortController?.signal?.aborted) {
      inFlight.delete(url);
      if (existing.counted) {
        existing.counted = false;
        if (existing.speculative) specActive = Math.max(0, specActive - 1);
        else navActive = Math.max(0, navActive - 1);
      }
    }
    if (cache.get(url)?.texture) return;

    const abortController =
      typeof AbortController === "function"
        ? new AbortController()
        : { abort() {}, signal: undefined };
    const owners = new Set(item.owners || []);
    if (item.ownerId != null) owners.add(item.ownerId);

    const job = {
      url,
      speculative,
      priority,
      owners,
      abortController,
      counted: true,
    };

    if (speculative) specActive += 1;
    else navActive += 1;

    const settleCounts = () => {
      if (!job.counted) return;
      job.counted = false;
      if (speculative) specActive = Math.max(0, specActive - 1);
      else navActive = Math.max(0, navActive - 1);
    };

    const promise = Promise.resolve(
      loadTexture(url, { signal: abortController.signal, speculative })
    )
      .then(texture => {
        if (inFlight.get(url) === job) inFlight.delete(url);
        settleCounts();
        rememberTexture(url, texture);
        pump();
        return texture;
      })
      .catch(err => {
        if (inFlight.get(url) === job) inFlight.delete(url);
        settleCounts();
        pump();
        throw err;
      });

    job.promise = promise;
    inFlight.set(url, job);
    promise.catch(() => {});
  };

  const enqueue = item => {
    const existing = queue.find(entry => entry.url === item.url);
    if (existing) {
      existing.priority = Math.min(existing.priority, item.priority);
      existing.speculative = existing.speculative && item.speculative;
      if (item.ownerId != null) {
        existing.owners = existing.owners || new Set();
        existing.owners.add(item.ownerId);
      }
      if (!item.speculative) existing.speculative = false;
      return;
    }
    queue.push({
      url: item.url,
      priority: item.priority,
      speculative: Boolean(item.speculative),
      ownerId: item.ownerId,
      owners: new Set(item.ownerId == null ? [] : [item.ownerId]),
    });
  };

  const pump = () => {
    queue.sort((a, b) => a.priority - b.priority);
    if (paused) return;
    while (queue.length) {
      const next = queue[0];
      if (cache.get(next.url)?.texture || inFlight.has(next.url)) {
        queue.shift();
        continue;
      }
      if (!canStart(next.speculative)) {
        if (!next.speculative) ensureNavSlot();
        if (!canStart(next.speculative)) break;
      }
      queue.shift();
      startJob(next);
    }
  };

  const request = (
    url,
    { ownerId, priority = PRIORITY.ACTIVE, speculative = false } = {}
  ) => {
    const key = normalizePanoramaUrl(url);
    if (!key) return Promise.reject(new Error("Missing panorama URL"));

    const cached = cache.get(key);
    if (cached?.texture) {
      acquire(key, ownerId);
      return Promise.resolve(cached.texture);
    }

    const flying = inFlight.get(key);
    if (flying && !flying.abortController?.signal?.aborted) {
      if (ownerId != null) flying.owners.add(ownerId);
      if (!speculative) {
        flying.speculative = false;
        flying.priority = PRIORITY.ACTIVE;
      } else {
        flying.priority = Math.min(flying.priority, priority);
      }
      return flying.promise.then(texture => {
        acquire(key, ownerId);
        return texture;
      });
    }
    if (flying?.abortController?.signal?.aborted) {
      inFlight.delete(key);
      if (flying.counted) {
        flying.counted = false;
        if (flying.speculative) specActive = Math.max(0, specActive - 1);
        else navActive = Math.max(0, navActive - 1);
      }
    }

    enqueue({
      url: key,
      ownerId,
      priority,
      speculative,
    });
    if (!speculative) ensureNavSlot();
    pump();

    const job = inFlight.get(key);
    if (job) {
      return job.promise.then(texture => {
        acquire(key, ownerId);
        return texture;
      });
    }

    return new Promise((resolve, reject) => {
      const wait = () => {
        const cachedNow = cache.get(key);
        if (cachedNow?.texture) {
          acquire(key, ownerId);
          resolve(cachedNow.texture);
          return;
        }
        const started = inFlight.get(key);
        if (started) {
          started.promise
            .then(texture => {
              acquire(key, ownerId);
              resolve(texture);
            })
            .catch(reject);
          return;
        }
        if (!queue.some(item => item.url === key)) {
          reject(new Error("Panorama request dropped"));
          return;
        }
        setTimeout(wait, 16);
      };
      wait();
    });
  };

  const setInterest = ({
    destinationUrl,
    linkedUrls = [],
    intentUrls = [],
    ownerId,
  } = {}) => {
    const destination = normalizePanoramaUrl(destinationUrl);
    const keep = new Set(
      [destination, ...linkedUrls, ...intentUrls]
        .map(normalizePanoramaUrl)
        .filter(Boolean)
    );

    for (let i = queue.length - 1; i >= 0; i -= 1) {
      const item = queue[i];
      if (!item.speculative) continue;
      if (keep.has(item.url)) continue;
      if (item.owners?.size && ownerId != null && !item.owners.has(ownerId)) {
        continue;
      }
      queue.splice(i, 1);
    }

    for (const [url, job] of inFlight) {
      if (!job.speculative) continue;
      if (keep.has(url)) continue;
      if (ownerId != null) job.owners.delete(ownerId);
      abortJob(url, "interest-changed");
    }

    linkedUrls.slice(0, TOUR_PERF.linkedPrefetchLimit).forEach(raw => {
      const url = normalizePanoramaUrl(raw);
      if (!url || url === destination || cache.get(url)?.texture) return;
      enqueue({
        url,
        ownerId,
        priority: PRIORITY.LINKED,
        speculative: true,
      });
    });

    intentUrls.slice(0, TOUR_PERF.intentPrefetchLimit).forEach(raw => {
      const url = normalizePanoramaUrl(raw);
      if (!url || url === destination || cache.get(url)?.texture) return;
      enqueue({
        url,
        ownerId,
        priority: PRIORITY.INTENT,
        speculative: true,
      });
    });

    pump();
  };

  const releaseOwner = ownerId => {
    if (ownerId == null) return;
    for (const [url, entry] of cache) {
      const count = entry.owners.get(ownerId) || 0;
      if (!count) continue;
      entry.refs = Math.max(0, entry.refs - count);
      entry.owners.delete(ownerId);
      void url;
    }
    for (let i = queue.length - 1; i >= 0; i -= 1) {
      const item = queue[i];
      item.owners?.delete(ownerId);
      if (item.ownerId === ownerId) item.ownerId = null;
      if (!item.owners?.size && item.speculative) queue.splice(i, 1);
    }
    for (const [url, job] of inFlight) {
      job.owners.delete(ownerId);
      abortJob(url, "owner-released");
    }
  };

  const nextWarmCandidate = (rendererId, urls = []) => {
    for (const raw of urls) {
      const url = normalizePanoramaUrl(raw);
      const entry = cache.get(url);
      if (!entry?.texture) continue;
      if (entry.warmed.has(rendererId)) continue;
      return { url, texture: entry.texture, entry };
    }
    return null;
  };

  const markWarmed = (url, rendererId) => {
    const entry = cache.get(normalizePanoramaUrl(url));
    if (entry) entry.warmed.add(rendererId);
  };

  const clearWarming = rendererId => {
    for (const entry of cache.values()) {
      entry.warmed.delete(rendererId);
      if (entry.texture) entry.texture.needsUpdate = true;
    }
  };

  const getCachedTexture = url => cache.get(normalizePanoramaUrl(url))?.texture ?? null;

  const peek = url => cache.get(normalizePanoramaUrl(url)) ?? null;

  const setPaused = value => {
    paused = Boolean(value);
    if (!paused) pump();
  };

  return {
    request,
    acquire,
    release,
    prune,
    setInterest,
    releaseOwner,
    nextWarmCandidate,
    markWarmed,
    clearWarming,
    getCachedTexture,
    peek,
    setPaused,
    cachedBytes,
    _debug: {
      cache,
      queue,
      inFlight,
      get navActive() {
        return navActive;
      },
      get specActive() {
        return specActive;
      },
    },
    isAbortError,
  };
};
